/**
 * The embedding app's auth token, handed over by postMessage.
 *
 * CC and OC plant a readable auth_token cookie, which App.js reads from
 * document.cookie. Sales Central (sole-seller-web) now sets that cookie
 * HttpOnly, which no script can read, so it posts the same token into this
 * iframe instead:
 *
 *   Arya   -> parent  { type: "ARYA_AUTH_REQUEST" }
 *   parent -> Arya    { type: "ARYA_AUTH_TOKEN", accessToken,
 *                       accessTokenExpiryTime, username }
 *
 * The fields are the auth_token cookie's, plus the username cookie's value,
 * and accessTokenExpiryTime is the same ISO date string. The parent may also
 * post ARYA_AUTH_TOKEN unprompted, e.g. on the iframe's load event.
 *
 * The token then goes to REACT_APP_AUTH_API as id_token, exactly as the
 * cookie's does, so the backend sees no difference.
 *
 * This module imports nothing: index.js loads it, and starts listening, before
 * config.json and MSAL, so a token posted that early is not lost.
 */

export const AUTH_REQUEST_TYPE = "ARYA_AUTH_REQUEST";
export const AUTH_TOKEN_TYPE = "ARYA_AUTH_TOKEN";

// How long to wait for the parent before falling back to SSO / custom login.
// Only paid in an iframe with no readable auth_token cookie.
export const PARENT_TOKEN_TIMEOUT_MS = 3000;

const nowSeconds = () => Math.floor(Date.now() / 1000);

const isFramed = () => window.parent !== window;

/**
 * The only pages that may hand Arya a token, or be asked for one: exact
 * origins, scheme and host with no path or trailing slash. The same image
 * serves every environment, so each environment's parent is listed.
 *
 * frame-ancestors (see .pipeline/security-headers.conf) is wider on purpose -
 * CC and OC embed Arya too and sign in by cookie - and it limits who can embed
 * Arya; it does nothing about who can postMessage to it.
 */
export const TRUSTED_PARENT_ORIGINS = [
	// Sales Central (/salescentral) and Credit Central (/cc) are both served
	// from these. An origin carries no path, so the two cannot be told apart.
	"https://www.piramalfinance.com", // prod
	"https://stage-web.piramalfinance.com", // stage
];

export function isTrustedParentOrigin(origin) {
	return TRUSTED_PARENT_ORIGINS.includes(origin);
}

/**
 * The parent page's origin when it is a trusted one, else null.
 *
 * location.ancestorOrigins (Chromium, Safari) is the browser's own record of
 * it; document.referrer, the page that loaded this iframe, stands in where
 * that is missing (Firefox). Neither has to be trusted for safety: postMessage
 * drops the message unless the parent's real origin matches.
 */
export function getTrustedParentOrigin() {
	let origin = window.location.ancestorOrigins?.[0];
	if (!origin) {
		try {
			origin = new URL(document.referrer).origin;
		} catch (error) {
			return null;
		}
	}
	return isTrustedParentOrigin(origin) ? origin : null;
}

/**
 * @returns {{idToken: string, exp: number, username: string} | null} the same
 *   shape App.js builds from the cookie, or null for anything else, including
 *   a token that has already expired.
 */
export function parseAuthTokenMessage(data) {
	if (!data || data.type !== AUTH_TOKEN_TYPE) return null;

	const { accessToken, accessTokenExpiryTime, username } = data;
	if (!accessToken || !accessTokenExpiryTime || !username) return null;

	const exp = Math.floor(new Date(accessTokenExpiryTime).getTime() / 1000);
	if (!Number.isFinite(exp) || exp <= nowSeconds()) return null;

	return { idToken: accessToken, exp, username };
}

// The newest valid token the parent has sent, and the requests waiting on one.
let latestToken = null;
const waiters = new Set();
let pendingRequest = null;
let listening = false;

function handleMessage(event) {
	if (event.source !== window.parent) return;
	if (!isTrustedParentOrigin(event.origin)) return;

	const token = parseAuthTokenMessage(event.data);
	if (!token) return;

	latestToken = token;
	for (const resolve of [...waiters]) resolve(token);
}

/** Start listening for the parent's token. Safe to call more than once. */
export function listenForParentToken() {
	if (listening || !isFramed()) return;
	listening = true;
	window.addEventListener("message", handleMessage);
}

/**
 * Ask the parent for its token.
 *
 * Only a parent on TRUSTED_PARENT_ORIGINS is asked, at its own origin.
 *
 * Resolves with { idToken, exp, username }, or null when Arya is not framed or
 * the parent sends nothing usable within timeoutMs - which is how every parent
 * that has not implemented this behaves, so the caller falls back to the
 * existing login flow. Concurrent calls share one request. Never rejects.
 */
export function requestParentToken(timeoutMs = PARENT_TOKEN_TIMEOUT_MS) {
	if (!isFramed()) return Promise.resolve(null);
	if (latestToken && latestToken.exp > nowSeconds()) {
		return Promise.resolve(latestToken);
	}
	if (pendingRequest) return pendingRequest;

	listenForParentToken();

	pendingRequest = new Promise((resolve) => {
		const settle = (token) => {
			clearTimeout(timer);
			waiters.delete(settle);
			pendingRequest = null;
			resolve(token);
		};
		const timer = setTimeout(() => settle(null), timeoutMs);
		waiters.add(settle);

		// Addressed to the parent's exact origin, never "*". With no trusted
		// origin to address, ask nothing and just wait: the parent may still
		// post its token unprompted, and handleMessage checks that reply.
		const parentOrigin = getTrustedParentOrigin();
		if (parentOrigin) {
			window.parent.postMessage({ type: AUTH_REQUEST_TYPE }, parentOrigin);
		}
	});
	return pendingRequest;
}
